import React from 'react';
import Link from 'next/link';
import Footer from '@/components/layout/Footer';
import { useI18n } from '@/i18n';

export default function ForgotPasswordPage() {
  const { t } = useI18n();
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    setError(null);
    setMessage(null);
    setSubmitting(true);

    try {
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = (await res.json().catch(() => null)) as
        | { success: boolean; message?: string }
        | null;

      if (!res.ok) {
        setError(data?.message || t('auth.forgotPassword.errors.requestFailed'));
        return;
      }

      setMessage(data?.message || t('auth.forgotPassword.success'));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('auth.forgotPassword.errors.requestFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col justify-center">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/images/login-hero.png')" }}
      ></div>
      <div className="absolute inset-0 gradient-light dark:gradient-dark opacity-80"></div>

      <div className="relative z-10 flex-grow flex items-center justify-center">
        <div className="bg-white dark:bg-neutral rounded-xl shadow-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold mb-6 text-center text-primary dark:text-secondary">
            {t('auth.forgotPassword.title')}
          </h1>
          {message ? (
            <div className="text-center space-y-4">
              <p className="text-sm text-slate-700 dark:text-slate-200">{message}</p>
              <Link href="/login" className="text-primary underline hover:no-underline">
                {t('common.actions.signIn')}
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}
              <input
                type="email"
                placeholder={t('common.labels.email')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary text-white p-3 rounded btn-hover btn-active"
              >
                {submitting
                  ? t('auth.forgotPassword.sending')
                  : t('auth.forgotPassword.submit')}
              </button>
              <Link href="/login" className="text-center text-sm text-primary hover:underline">
                {t('common.actions.backToSignIn')}
              </Link>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
